/**
 * Avatar — the one place that renders a person's avatar across the app.
 *
 * Shows the uploaded image when `avatarUrl` is set (a cropped square data URL,
 * same storage pattern as the company logo), else a brand-colored initials
 * bubble. Used by the header user menu, the comment thread, and the @mention
 * autocomplete — so the treatment stays identical everywhere. Sized in px so
 * each surface can ask for the diameter it needs without bespoke classes.
 */

export function initialsOf(name: string): string {
  return name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

interface AvatarProps {
  name?: string | null;
  avatarUrl?: string | null;
  /** Diameter in px. Defaults to 28 (header + comment-thread size). */
  size?: number;
  /** Extra classes (e.g. a ring on the header). */
  className?: string;
}

export default function Avatar({ name, avatarUrl, size = 28, className = '' }: AvatarProps) {
  const dim = { width: size, height: size };
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name ?? ''}
        style={dim}
        className={`rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }
  return (
    <div
      style={{ ...dim, fontSize: Math.round(size * 0.36) }}
      className={`rounded-full bg-brand-primary text-white flex items-center justify-center font-bold flex-shrink-0 ${className}`}
    >
      {initialsOf(name ?? '?')}
    </div>
  );
}
